/* eslint-disable react/jsx-props-no-spreading */

import {
  AudienceInstance,
  Group,
  Segmentation,
  User,
} from '@/client/services/api/graphql/gql/graphql';
import {
  Avatar,
  Box,
  Circle,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CiCircleCheck, CiCirclePlus } from 'react-icons/ci';
import { IoGitNetworkOutline, IoLocationOutline } from 'react-icons/io5';
import { useEffect, useState } from 'react';

import AdminAssignmentsService from '../api/AdminAssignmentsService';
import type { AssignmentFormData } from '../types/assignmentTypes';
import { DefaultTFuncReturn } from 'i18next';
import { GoOrganization } from 'react-icons/go';
import HelpTextPopover from '@/client/components/admin/assignments/builder/overlay/HelpTextPopover';
import { HiOutlineUserGroup } from 'react-icons/hi2';
import { IconType } from 'react-icons/lib';
import { LiaUserGraduateSolid } from 'react-icons/lia';
import NoSearchResultsListItem from '../../create/content/lists/NoSearchResultsListItem';
import { Search2 } from '@/client/components/icons/ContinuIcons';
import { useAuthStore } from '@/client/services/state/authStore';
import { useCombobox } from 'downshift';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface AudienceSearchProps {
  label: string;
  helpText: string;
  handleSelectItem: (item: User & Segmentation & Group) => void;
  selectedItems: AudienceInstance[];
  hasError: boolean;
  includeUsers?: boolean;
  onFileUpload?: () => void;
  tooltipText?: string;
  placeholder?: string | DefaultTFuncReturn | undefined;
}

export default function AudienceSearch({
  label,
  helpText,
  handleSelectItem,
  selectedItems,
  hasError,
  includeUsers = false,
  onFileUpload = undefined,
  tooltipText = undefined,
  placeholder = undefined,
}: AudienceSearchProps) {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { company } = authConfig;
  const { clearErrors } = useFormContext<AssignmentFormData>();
  const [searchTerm, setSearchTerm] = useState<string | undefined>('');

  useEffect(() => {
    if (searchTerm) {
      clearErrors('audience');
    }
  }, [searchTerm]);

  const typeIconMap: { [key: string]: IconType } = {
    location: IoLocationOutline,
    department: IoGitNetworkOutline,
    team: HiOutlineUserGroup,
    org_level: GoOrganization,
    grade: LiaUserGraduateSolid,
    group: HiOutlineUserGroup,
  };

  const { data, isFetching } = useQuery({
    enabled: !!searchTerm && searchTerm.length > 0,
    queryKey: ['audienceSearch', searchTerm, company.groups],
    queryFn: async () => AdminAssignmentsService.audienceSearch(searchTerm!, company.groups),
    select: (selectData) => {
      const options: { label: string; options: (User | Segmentation | Group)[] }[] = [
        {
          label: t('admin.assignments.audience.search.locations'),
          options: selectData.segmentations.filter(
            (segmentation) => segmentation.segmentationType === 'location',
          ),
        },
        {
          label: t('admin.assignments.audience.search.departments'),
          options: selectData.segmentations.filter(
            (segmentation) => segmentation.segmentationType === 'department',
          ),
        },
        {
          label: t('admin.assignments.audience.search.teams'),
          options: selectData.segmentations.filter(
            (segmentation) => segmentation.segmentationType === 'team',
          ),
        },
        {
          label: t('admin.assignments.audience.search.grades'),
          options: selectData.segmentations.filter(
            (segmentation) => segmentation.segmentationType === 'grade',
          ),
        },
        {
          label: t('admin.assignments.audience.search.orgLevels'),
          options: selectData.segmentations.filter(
            (segmentation) => segmentation.segmentationType === 'org_level',
          ),
        },
      ];

      if (includeUsers)
        options.unshift({
          label: t('admin.assignments.audience.search.users'),
          options: selectData.users,
        });

      if (company.groups)
        options.push({
          label: t('admin.assignments.audience.search.groups'),
          options: selectData.groups.map((group) => ({
            ...group,
            type: 'segment',
            segmentationType: 'group',
          })),
        });

      return options;
    },
  });

  const flattenGroupOptions = (
    options: { label: string; options: (User | Segmentation | Group)[] }[],
  ) =>
    options.reduce(
      (
        prev: (User | Segmentation | Group)[],
        curr: { label: string; options: (User | Segmentation | Group)[] },
      ) => [...prev, ...curr.options],
      [],
    );

  const { getMenuProps, getInputProps, highlightedIndex, getItemProps } = useCombobox({
    onInputValueChange({ inputValue }) {
      setSearchTerm(inputValue);
    },
    items: data ? flattenGroupOptions(data) : [],
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      handleSelectItem(selectedItem as User & Segmentation & Group);

      setSearchTerm('');
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
            inputValue: '', // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '', // don't add the item string as input value at selection.
          };
        default:
          return changes;
      }
    },
  });

  return (
    <Stack spacing={0}>
      <Stack spacing={0} marginBottom={2}>
        <HStack>
          <Text variant="createLabel" color={hasError ? 'error' : 'baseBlack'}>
            {label}
          </Text>

          {tooltipText && (
            <HelpTextPopover
              popoverContent={
                <Text fontSize="14px" fontWeight={400} lineHeight="160%">
                  {tooltipText}
                </Text>
              }
            />
          )}
        </HStack>

        <Flex justifyContent="space-between">
          <Text
            variant="createHelpText"
            maxWidth="80%"
            color={hasError ? 'error' : 'brand.legibleBlack.60'}
          >
            {helpText}
          </Text>

          {onFileUpload && (
            <Text
              fontSize="14px"
              fontWeight={400}
              textDecoration="underline"
              _hover={{
                cursor: 'pointer',
              }}
              onClick={onFileUpload}
            >
              {t('admin.assignments.audience.uploadFile')}
            </Text>
          )}
        </Flex>
      </Stack>

      <Stack spacing={0}>
        <InputGroup>
          <InputLeftElement>
            <Search2 color="neutral.500" />
          </InputLeftElement>

          <Input
            {...getInputProps()}
            value={searchTerm}
            variant="create"
            isInvalid={hasError}
            placeholder={placeholder ?? t('admin.assignments.audience.search.placeholder')}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <InputRightElement>{isFetching && <Spinner size="sm" />}</InputRightElement>
        </InputGroup>

        <List
          marginTop={2}
          flexDirection="column"
          border={data ? '1px solid' : 'none'}
          borderColor="warmNeutral.500"
          borderRadius="md"
          background="white"
          overflowY="scroll"
          paddingX={6}
          zIndex={9999}
          maxH={400}
          width="inherit"
          {...getMenuProps()}
        >
          {data?.length === 0 && <NoSearchResultsListItem />}

          {data &&
            data.length > 0 &&
            data
              .filter((section) => section.options.length > 0)
              .reduce(
                (results: any, section: any, sectionIndex: any) => {
                  results.sections.push(
                    <Box
                      key={`results_section_${sectionIndex + 1}`}
                      paddingY={4}
                      borderBottom="1px solid"
                      borderColor="warmNeutral.100"
                    >
                      <Text variant="createLabel" marginY={2}>
                        {section.label}
                      </Text>

                      {section.options.map(
                        (option: User & Segmentation & Group, optionIndex: number) => {
                          const isSelected = selectedItems.find((item) => item.id === option.id);

                          // eslint-disable-next-line no-plusplus, no-param-reassign
                          const resultIndex = results.itemIndex++;

                          return (
                            <ListItem
                              key={`option_index_${optionIndex + 1}`}
                              paddingX={4}
                              paddingY={2}
                              _hover={{
                                cursor: 'pointer',
                              }}
                              {...getItemProps({
                                item: option,
                                index: resultIndex,
                                'aria-selected': !!isSelected,
                              })}
                              backgroundColor={
                                highlightedIndex === resultIndex ? 'warmNeutral.0' : 'white'
                              }
                              border={highlightedIndex === resultIndex ? '1px solid' : 'none'}
                              borderColor="warmNeutral.400"
                              borderRadius="6px"
                            >
                              <Flex alignItems="center" width="full" justifyContent="space-between">
                                {option.__typename === 'Segmentation' && (
                                  <Circle
                                    backgroundColor={isSelected ? 'gray.50' : 'gray.100'}
                                    size="30px"
                                  >
                                    <Icon
                                      color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                                      as={typeIconMap[option.segmentationType]}
                                    />
                                  </Circle>
                                )}

                                {option.__typename === 'Group' && (
                                  <Circle
                                    backgroundColor={isSelected ? 'gray.50' : 'gray.100'}
                                    size="30px"
                                  >
                                    <Icon
                                      color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                                      as={HiOutlineUserGroup}
                                    />
                                  </Circle>
                                )}

                                {option.__typename === 'User' && (
                                  <Avatar
                                    size="sm"
                                    name={option.fullName || option.name}
                                    src={option.image as string}
                                  />
                                )}

                                <Box flexGrow={1} textAlign="left" paddingLeft={4}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight={500}
                                    color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                                  >
                                    {option.fullName || option.name}
                                  </Text>
                                </Box>

                                <Icon
                                  boxSize={8}
                                  as={isSelected ? CiCircleCheck : CiCirclePlus}
                                  color={isSelected ? 'warmNeutral.300' : 'neutral.800'}
                                />
                              </Flex>
                            </ListItem>
                          );
                        },
                      )}
                    </Box>,
                  );

                  return results;
                },
                { sections: [], itemIndex: 0 },
              ).sections}
        </List>
      </Stack>
    </Stack>
  );
}
